import { Dropdown } from "primereact/dropdown";
import { FileUpload } from "primereact/fileupload";
import { Toast } from "primereact/toast";
import React from "react";
import { SeatMapService, VenueService } from "src/app/services/Index";
import { environment } from "src/environments/environment";
import "./AdminSeatMaps.css";

type State = {
	venues: any[];
	venueId: number | null;
	map: any;
	loading: boolean;
	saving: boolean;
};

/**
 * The seating plan for a ground: the drawing, and which block is which.
 *
 * Our seat model records the block a seat belongs to and never where it is, so
 * a plan cannot be generated — a club supplies one. That makes the element ids
 * inside the drawing a contract with the data, and keeping the two in step is
 * what this screen is for.
 *
 * Both directions of drift are shown, because neither surfaces anywhere else
 * until a supporter complains: a block pointing at an element that isn't in the
 * drawing is invisible and unsellable, and an element with no block is a stand
 * nobody can buy in.
 */
export class AdminSeatMaps extends React.Component<{}, State> {
	private toast: Toast | null = null;

	constructor(props: {}) {
		super(props);
		this.state = { loading: false, map: null, saving: false, venueId: null, venues: [] };
	}

	componentDidMount() {
		this.loadVenues();
	}

	async loadVenues() {
		try {
			const res = await VenueService.getAll();
			if (!res.ok) return;
			const venues = await res.json();
			this.setState({ venues }, () => {
				if (venues.length) this.pick(venues[0].id);
			});
		} catch {
			this.tell("error", "Couldn't load venues.");
		}
	}

	pick(venueId: number) {
		this.setState({ loading: true, venueId }, () => this.loadMap());
	}

	async loadMap() {
		const { venueId } = this.state;
		if (!venueId) return;
		try {
			const res = await SeatMapService.get(venueId);
			this.setState({ loading: false, map: res.ok ? await res.json() : null });
		} catch {
			this.setState({ loading: false });
			this.tell("error", "Couldn't load that seating plan.");
		}
	}

	async upload(event: any) {
		const { venueId } = this.state;
		if (!venueId) return;

		try {
			const form = new FormData();
			form.append("file", event.files[0]);

			const res = await SeatMapService.upload(venueId, form);
			if (!res.ok) {
				this.tell("error", (await res.text()).replace(/^"|"$/g, "") || "That didn't upload.");
				return;
			}

			const body = await res.json();

			// The API returns where it stored the file; the URL is composed here
			// from the configured storage base, the same as every other upload.
			const url = `${environment.server.storageUrl}/${body.location}`;
			await SeatMapService.setUrl(venueId, url);

			this.tell("success", body.bytesRemoved > 0
				? `Uploaded. ${body.bytesRemoved} bytes of scripting were stripped out.`
				: "Uploaded.");

			this.loadMap();
		} catch {
			this.tell("error", "That didn't upload. Please try again.");
		}
	}

	async saveMapping(blockId: number, svgElementId: string | null) {
		this.setState({ saving: true });
		try {
			const res = await SeatMapService.map(this.state.venueId!, [{ blockId, svgElementId }]);
			if (!res.ok) {
				this.tell("error", (await res.text()).replace(/^"|"$/g, "") || "Couldn't save that.");
				return;
			}
			this.loadMap();
		} catch {
			this.tell("error", "Couldn't save that.");
		} finally {
			this.setState({ saving: false });
		}
	}

	tell(severity: "success" | "error", detail: string) {
		this.toast?.show({ closable: false, detail, life: 4000, severity, summary: "Seating plan" });
	}

	render() {
		const { venues, venueId, map, loading } = this.state;

		return (
			<div className="sm-page">
				<Toast ref={(el) => { this.toast = el; }} />

				<div className="sm-bar">
					<Dropdown
						onChange={(e) => this.pick(e.value)}
						optionLabel="name"
						optionValue="id"
						options={venues}
						placeholder="Pick a venue"
						value={venueId}
					/>
					<FileUpload
						accept=".svg,image/svg+xml"
						auto
						chooseLabel={map?.svgUrl ? "Replace plan" : "Upload plan"}
						customUpload
						mode="basic"
						uploadHandler={(e) => { this.upload(e); (e.options as any)?.clear?.(); }}
					/>
				</div>

				{loading && <p className="sm-muted">Loading…</p>}

				{!loading && map && !map.svgUrl &&
					<div className="sm-empty">
						<h3>No seating plan yet</h3>
						<p>
							Upload an SVG of the ground. Give each stand an <code>id</code> in your drawing
							package &mdash; those are what you map the blocks onto below.
						</p>
					</div>}

				{!loading && map?.svgUrl && <>
					<div className="sm-preview">
						{/* An <img> here on purpose: this is a preview, nothing is
						    clicked, and there is no reason to inline foreign markup
						    where it doesn't buy anything. */}
						<img alt="Seating plan" src={map.svgUrl} />
					</div>

					{map.unmappedElements?.length > 0 &&
						<div className="sm-warn">
							<strong>{map.unmappedElements.length} part{map.unmappedElements.length === 1 ? "" : "s"} of the drawing
								{map.unmappedElements.length === 1 ? " isn't" : " aren't"} mapped to a block:</strong>{" "}
							{map.unmappedElements.join(", ")} &mdash; nobody can buy a seat there.
						</div>}

					<table className="sm-table">
						<thead>
							<tr>
								<th>Block</th>
								<th>Area</th>
								<th>Seats</th>
								<th>Part of the drawing</th>
							</tr>
						</thead>
						<tbody>
							{map.blocks.map((b: any) => (
								<tr className={b.missingFromDrawing ? "sm-bad" : ""} key={b.blockId}>
									<td>{b.blockName}</td>
									<td className="sm-muted">{b.areaName}</td>
									<td className="sm-muted">{b.seats}</td>
									<td>
										<Dropdown
											onChange={(e) => this.saveMapping(b.blockId, e.value)}
											options={[{ label: "— not mapped —", value: null },
												...(map.elementIds || []).map((id: string) => ({ label: id, value: id }))]}
											placeholder="Pick a part"
											showClear
											value={b.svgElementId}
										/>
										{b.missingFromDrawing &&
											<div className="sm-bad-note">
												&ldquo;{b.svgElementId}&rdquo; isn&rsquo;t in the drawing &mdash; this block is invisible
												and its {b.seats} seats can&rsquo;t be sold.
											</div>}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</>}
			</div>
		);
	}
}
